/**
 * Admin "Training" panel — surfaces the consultant training pack.
 *
 *   · Shows pack metadata (chapter count, approx word count, file list).
 *   · Two download buttons that hit the gated backend endpoints
 *     `/admin/training/pack.pdf` and `/admin/training/pack.docx`.
 *
 * The packs are rendered on demand server-side, so consultants always get
 * the latest version of the markdown sources without us having to ship a
 * fresh build every time we tweak a chapter.
 */
import React, { useCallback, useEffect, useState } from "react";
import { FileDown, FileText, BookOpen, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { API_BASE } from "../../lib/api";

const downloadFromAdmin = async (path, filename, setBusy) => {
  setBusy(true);
  try {
    const res = await fetch(`${API_BASE}${path}`, { credentials: "include" });
    if (!res.ok) {
      const err = await res.text().catch(() => "");
      throw new Error(err || `HTTP ${res.status}`);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${filename}`);
  } catch (e) {
    toast.error(`Download failed: ${e.message || e}`);
  } finally {
    setBusy(false);
  }
};

export default function TrainingPanel() {
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyPdf, setBusyPdf] = useState(false);
  const [busyDocx, setBusyDocx] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/training/metadata");
      setMeta(data);
    } catch {
      toast.error("Failed to load training metadata");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div data-testid="admin-training-panel">
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="ek-heading text-xl">Consultant training pack</h2>
          <p className="ek-text-muted text-[13px] mt-1 max-w-2xl">
            The full EKSELENS360 consultant training pack — engine mechanics, running a diagnostic, interpreting outputs, lite assessments, framework catalogue, scripts, admin operations and troubleshooting — rendered on demand from <code className="text-[12px] ek-text-soft">/app/docs/training/</code> into a branded PDF or Microsoft Word document.
          </p>
        </div>
        <button
          type="button"
          onClick={reload}
          className="ek-text-muted hover:text-black text-[12px] flex items-center gap-1"
          data-testid="training-refresh"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8">
        <div className="border ek-line ek-surface p-5">
          <div className="text-[10.5px] font-medium tracking-[0.18em] ek-text-muted uppercase mb-2">Chapters</div>
          <div className="text-3xl font-medium text-[#7737F6] tabular-nums" data-testid="training-chapter-count">
            {loading ? "…" : meta?.chapter_count ?? "—"}
          </div>
        </div>
        <div className="border ek-line ek-surface p-5">
          <div className="text-[10.5px] font-medium tracking-[0.18em] ek-text-muted uppercase mb-2">Approx. words</div>
          <div className="text-3xl font-medium text-[#7737F6] tabular-nums" data-testid="training-word-count">
            {loading ? "…" : meta?.approx_words?.toLocaleString("en-GB") ?? "—"}
          </div>
        </div>
        <div className="border ek-line ek-surface p-5">
          <div className="text-[10.5px] font-medium tracking-[0.18em] ek-text-muted uppercase mb-2">Pack version</div>
          <div className="text-3xl font-medium text-[#7737F6]">v1</div>
          <div className="text-[11px] ek-text-muted mt-1">Feb 2026 · regenerated each download</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
        <div className="border ek-line ek-surface p-6 flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-2 mb-3 text-[#7737F6]">
              <FileDown size={18} />
              <h3 className="text-[13px] font-semibold tracking-[0.18em] uppercase">Download as PDF</h3>
            </div>
            <p className="ek-text-soft text-[13px] mb-4">
              Branded A4 PDF (~40 pages) with cover, contents and every chapter. Renders server-side via WeasyPrint — guaranteed identical for every consultant.
            </p>
          </div>
          <button
            type="button"
            onClick={() => downloadFromAdmin("/admin/training/pack.pdf", `ekselens360-training-pack-${today}.pdf`, setBusyPdf)}
            disabled={busyPdf}
            className="ek-btn ek-btn-primary text-[12.5px] disabled:opacity-50 w-fit"
            data-testid="training-download-pdf"
          >
            <FileDown size={13} /> {busyPdf ? "Rendering…" : "Download PDF"}
          </button>
        </div>

        <div className="border ek-line ek-surface p-6 flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-2 mb-3 text-[#7737F6]">
              <FileText size={18} />
              <h3 className="text-[13px] font-semibold tracking-[0.18em] uppercase">Download as Word</h3>
            </div>
            <p className="ek-text-soft text-[13px] mb-4">
              Microsoft Word document with cover, contents and every chapter. Edit-friendly if you want to fork a tailored version for a specific engagement — but keep the master copy here.
            </p>
          </div>
          <button
            type="button"
            onClick={() => downloadFromAdmin("/admin/training/pack.docx", `ekselens360-training-pack-${today}.docx`, setBusyDocx)}
            disabled={busyDocx}
            className="ek-btn ek-btn-secondary text-[12.5px] disabled:opacity-50 w-fit"
            data-testid="training-download-docx"
          >
            <FileText size={13} /> {busyDocx ? "Rendering…" : "Download Word"}
          </button>
        </div>
      </div>

      <div>
        <div className="flex items-center gap-2 mb-3">
          <BookOpen size={14} className="ek-text-muted" />
          <h3 className="text-[12px] font-semibold tracking-[0.18em] uppercase ek-text-muted">Chapters in this pack</h3>
        </div>
        <div className="border ek-line ek-surface">
          {loading ? (
            <div className="px-4 py-6 ek-text-muted text-[13px]">Loading…</div>
          ) : !meta?.chapters?.length ? (
            <div className="px-4 py-6 ek-text-muted text-[13px]">No chapters found.</div>
          ) : (
            <ol className="divide-y divide-zinc-100">
              {meta.chapters.map((c, i) => (
                <li
                  key={c.file}
                  className="flex items-baseline gap-4 px-4 py-3 text-[13px]"
                  data-testid={`training-chapter-${i}`}
                >
                  <span className="text-[11px] font-semibold tracking-[0.18em] text-[#7737F6] tabular-nums w-8">
                    {i === 0 ? "—" : String(i).padStart(2, "0")}
                  </span>
                  <span className="flex-1 ek-text">{c.title}</span>
                  <code className="text-[11px] ek-text-muted">{c.file}</code>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}
